import { FC, useState } from 'react';
import ReusableModal from '../common/Modal';
import { Box, Button, FormControl, FormLabel, TextField } from '@mui/material';
import { commonStyles } from '../../utils/styles/inputs';
import MessagePopup from '../common/MessagePopup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAlert } from '../../hooks/useAlert';
import {
  addLabelToVersionApi,
  addConversationFlowLabelApi,
} from '../../utils/apis';

interface AddLabelToVersionHistoryModalProps {
  open: boolean;
  onClose: () => void;
  versionId: string;
  selectedView: 'single' | 'advance';
}

const AddLabelToVersionHistoryModal: FC<AddLabelToVersionHistoryModalProps> = ({
  open,
  onClose,
  versionId,
  selectedView,
}) => {
  const queryClient = useQueryClient();
  const { alert, setAlert, showAlert } = useAlert();
  const [labels, setLabels] = useState<string>('');

  const { mutateAsync: addLabels, isPending } = useMutation({
    mutationFn: (data: { versionId: string; label: string[] }) => {
      if (selectedView === 'single') {
        return addLabelToVersionApi(data);
      } else {
        return addConversationFlowLabelApi(data.versionId, data.label);
      }
    },
    onSuccess: () => {
      showAlert('Labels added successfully', 'success');
      queryClient.invalidateQueries({
        queryKey: ['get-version-history'],
      });
      queryClient.invalidateQueries({
        queryKey: ['get-version-history-sidebar'],
      });
      queryClient.invalidateQueries({
        queryKey: ['get-conversation-flow-versions'],
      });
      onClose();
    },
    onError: (error: any) => {
      showAlert(
        error?.response?.data?.message || 'Failed to add labels',
        'error'
      );
    },
  });

  const handleSubmit = async () => {
    const labelArray = labels
      .split(',')
      .map(label => label.trim())
      .filter(Boolean);

    await addLabels({ versionId, label: labelArray });
  };

  return (
    <>
      <ReusableModal open={open} onClose={onClose} title="Add Labels">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth>
            <FormLabel sx={commonStyles.formLabel}>
              Enter Labels (comma separated)
            </FormLabel>
            <TextField
              id="labels"
              name="labels"
              placeholder="label1, label2, label3"
              required
              fullWidth
              variant="outlined"
              sx={commonStyles.textField}
              value={labels}
              onChange={e => setLabels(e.target.value)}
            />
          </FormControl>

          <Button
            variant="contained"
            sx={{ width: '100%', textTransform: 'none' }}
            disabled={!labels || isPending}
            onClick={() => void handleSubmit()}
          >
            {isPending ? 'Adding...' : 'Add Labels'}
          </Button>
        </Box>
      </ReusableModal>
      <MessagePopup alert={alert} setAlert={setAlert} />
    </>
  );
};

export default AddLabelToVersionHistoryModal;
